import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Button,
  Tabs,
  Tab,
  Alert,
  DropdownButton,
  MenuItem,
} from "react-bootstrap";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import { push } from "connected-react-router";
import Sticky from "react-stickynode";

import TitleBar from "@common/components/TitleBar/TitleBar";
import BeerProfileBar from "@common/components/BeerProfileBar/BeerProfileBar";
import BrewHistory from "@common/components/BrewHistory/BrewHistory";
import RecipeVersions from "./RecipeVersions/RecipeVersions";
import Mashing from "./RecipeDetails/RecipeDetailMashing";
import Boil from "./RecipeDetails/RecipeDetailBoil";
import Fermentation from "./RecipeDetails/RecipeDetailFerm";
import BrewWater from "../CreateRecipe/Recipe/BrewWater/BrewWater";
import BrewModal from "@common/components/BrewModal/BrewModal";
import { StartFermentationModal } from "@brewery/pages/Brewery/StartFermentationModal/StartFermentationModal";
import NoRecipesFound from "./NoRecipesFound/NoRecipesFound";
import Divider from "@common/components/Divider/Divider";
import RecipeDetailLoading from "./RecipeDetailLoading/RecipeDetailLoading";
import { ACCESS_TYPES } from "@common/constants/accessTypes";
import { recipeFitsKeg } from "@brewery/pages/Brewery/EditFermentationModal/recipeFermentation";
import * as recipeService from "@beers/services/recipeService";
import * as sessionService from "@brewery/services/sessionService";
import * as deviceService from "@common/services/deviceService";
import EditModal from "./EditModal";
import CloneModal from "./CloneModal";
import "./recipe-detail.scss";

export function RecipeDetail() {
  const dispatch = useDispatch();
  const { id: beerIdParam, version: versionParam } = useParams();

  const userInfo = useSelector((state) => state.userInfo);
  const loadingBar = useSelector((state) => state.loadingBar);
  const accessType = userInfo?.user?.access_type;

  const [beer, setBeer] = useState({});
  const [recipes, setRecipes] = useState([]);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [beerLoaded, setBeerLoaded] = useState(false);
  const [recipeLoaded, setRecipeLoaded] = useState(false);
  const [showBrewModal, setShowBrewModal] = useState(false);
  const [showFermentationModal, setShowFermentationModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [activeNoteKey, setActiveNoteKey] = useState(1);
  const [mashInWater, setMashInWater] = useState(0);

  const calculateMashInWater = (recipe) => {
    let total = 0;
    if (recipe && Array.isArray(recipe.mashing)) {
      recipe.mashing.forEach((step) => {
        total += +step.mash_in_water || 0;
      });
    }
    return total;
  };

  const getSessionsForRecipe = useCallback(
    async (recipeId) => {
      if (!recipeId) return;
      dispatch(showLoading());
      try {
        const res = await sessionService.getSessionsByRecipeId(recipeId);
        setSessions(res?.results ? res.results : res || []);
      } catch (err) {
        setSessions([]);
      } finally {
        dispatch(hideLoading());
      }
    },
    [dispatch],
  );

  const init = useCallback(async () => {
    if (!beerIdParam) return;

    dispatch(showLoading());
    try {
      const beerRes = await recipeService.getBeerById(beerIdParam);
      setBeer(beerRes);
      setBeerLoaded(true);
    } catch (err) {
      setBeer({});
      setBeerLoaded(true);
    } finally {
      dispatch(hideLoading());
    }

    dispatch(showLoading());
    try {
      const recipesRes = (await recipeService.getRecipes(beerIdParam)) || [];
      const sortedRecipes = [...recipesRes].sort((a, b) => a.id - b.id);
      setRecipes(sortedRecipes);

      let targetRecipe = sortedRecipes[sortedRecipes.length - 1] || null;
      if (versionParam && sortedRecipes.length) {
        const found = sortedRecipes.find(
          (recipe) => +recipe.version_name === +versionParam,
        );
        if (found) targetRecipe = found;
      }

      setCurrentRecipe(targetRecipe);
      setMashInWater(calculateMashInWater(targetRecipe));
      setRecipeLoaded(true);

      if (targetRecipe) {
        getSessionsForRecipe(targetRecipe.id);
      }
    } catch (err) {
      setRecipes([]);
      setCurrentRecipe(null);
      setRecipeLoaded(true);
    } finally {
      dispatch(hideLoading());
    }
  }, [beerIdParam, versionParam, dispatch, getSessionsForRecipe]);

  useEffect(() => {
    init();
  }, [init]);

  // Sync versionParam changes when route changes without full re-fetch
  useEffect(() => {
    if (versionParam && recipes.length) {
      const found = recipes.find(
        (recipe) => +recipe.version_name === +versionParam,
      );
      if (found && found.id !== currentRecipe?.id) {
        setCurrentRecipe(found);
        setMashInWater(calculateMashInWater(found));
        getSessionsForRecipe(found.id);
      }
    }
  }, [versionParam, recipes, currentRecipe?.id, getSessionsForRecipe]);

  const setRecipe = (recipeID) => {
    const recipe = recipes.find((rec) => rec.id === recipeID);
    if (!recipe) return;
    setCurrentRecipe(recipe);
    setMashInWater(calculateMashInWater(recipe));
    getSessionsForRecipe(recipe.id);
    dispatch(push(`/beers/${recipe.beer_id}/version/${recipe.version_name}`));
  };

  const routeToEdit = () => {
    if (!beer?.id || !currentRecipe?.id) return;
    const route = `/beers/${beer.id}/edit/${currentRecipe.id}?mode=validate`;
    dispatch(push(route));
  };

  const routeToNewVersion = () => {
    if (!beer?.id || !currentRecipe?.id) return;
    const route = `/beers/${beer.id}/newversion/${currentRecipe.id}?mode=validate`;
    dispatch(push(route));
  };

  const alreadyBrewed = () => !!currentRecipe?.times_brewed;

  const shareRecipe = async () => {
    if (!currentRecipe) return;
    try {
      await recipeService.toggleShareRecipe(currentRecipe);
      init();
    } catch (err) {
      // handle error
    }
  };

  const openStartBrewModal = () => {
    deviceService.getDevices().catch(() => {});
    setShowBrewModal(true);
  };

  const openStartFermentationModal = () => {
    setShowFermentationModal(true);
  };

  const isPageReady = beerLoaded && recipeLoaded && recipes.length > 0;
  const noteType = activeNoteKey === 1 ? "private_note" : "public_note";

  const fermEligibility = useMemo(() => {
    return currentRecipe
      ? recipeFitsKeg(currentRecipe)
      : { ok: false, reason: "No recipe" };
  }, [currentRecipe]);

  const header = (number, stageName) => (
    <h2>
      <span className="step">{number}</span>
      &nbsp;
      <span className="step-title"> {stageName} </span>
    </h2>
  );

  return (
    <div>
      {isPageReady && currentRecipe && (
        <div>
          <TitleBar
            title={(beer?.name || "").toUpperCase()}
            subtitle={beer?.style?.name}
            displayGoBack={true}
            goBack={() => history.goBack()}
          />
          <Sticky enabled={true} top={80} innerZ={100}>
            <BeerProfileBar
              beerProfile={{
                ...currentRecipe,
                carbonation: currentRecipe?.carbonation || beer?.carbonation,
              }}
            />
          </Sticky>
          <div className="recipe-detail container-fluid">
            <div className="row recipe-detail__container">
              <div className="col-sm-3 col-md-3" style={{ padding: "0px" }}>
                <RecipeVersions
                  selectedRecipe={currentRecipe}
                  recipes={recipes}
                  handleClick={setRecipe}
                />
              </div>
              <div className="col-xs-12 col-sm-9 col-md-9 recipe-detail__container__brewdata">
                <div className="row">
                  <Tabs id="recipe-detail-tabs">
                    <Tab eventKey={1} title="RECIPE">
                      <div className="col-md-10 col-md-offset-1">
                        <div className="row recipe-detail__header-row">
                          <div className="col-xs-12 col-md-4">
                            <h1>VERSION {currentRecipe.version_name}</h1>
                          </div>
                          <div className="col-xs-12 col-md-8 recipe-detail__actions">
                            <Button
                              bsStyle="default"
                              className="hidden-xs"
                              onClick={shareRecipe}
                              disabled={
                                !currentRecipe.brewable && !currentRecipe.shared
                              }
                            >
                              {currentRecipe.shared ? "UNSHARE" : "SHARE"}
                            </Button>
                            <Button
                              className="hidden-xs"
                              title={
                                alreadyBrewed()
                                  ? "Edit beer details & notes"
                                  : "Edit recipe"
                              }
                              onClick={
                                alreadyBrewed()
                                  ? () => setShowEditModal(true)
                                  : routeToEdit
                              }
                            >
                              EDIT
                            </Button>
                            <Button
                              bsStyle="default"
                              className="hidden-xs"
                              onClick={routeToNewVersion}
                            >
                              NEW VERSION
                            </Button>
                            <Button
                              bsStyle="success"
                              className="recipe-detail__btn-ferm"
                              onClick={openStartFermentationModal}
                              disabled={!fermEligibility.ok}
                              title={
                                fermEligibility.ok
                                  ? "Start custom fermentation on a MiniBrew Keg"
                                  : fermEligibility.reason ||
                                    "Fermentation not available"
                              }
                            >
                              START FERMENTATION
                            </Button>
                            <Button
                              bsStyle="primary"
                              className="recipe-detail__btn-brew"
                              onClick={openStartBrewModal}
                              disabled={
                                !currentRecipe.brewable &&
                                accessType !== ACCESS_TYPES.BP_RESEARCHER
                              }
                            >
                              BREW
                            </Button>
                            <DropdownButton
                              pullRight
                              title=""
                              id="more-options"
                              className="recipe-detail__dropdown"
                            >
                              <MenuItem
                                className="visible-xs"
                                onClick={shareRecipe}
                                disabled={
                                  !currentRecipe.brewable &&
                                  !currentRecipe.shared
                                }
                              >
                                {currentRecipe.shared ? "Unshare" : "Share"}
                              </MenuItem>
                              <MenuItem
                                className="visible-xs"
                                onClick={
                                  alreadyBrewed()
                                    ? () => setShowEditModal(true)
                                    : routeToEdit
                                }
                              >
                                Edit
                              </MenuItem>
                              <MenuItem
                                className="visible-xs"
                                onClick={routeToNewVersion}
                              >
                                New version
                              </MenuItem>
                              <MenuItem divider className="visible-xs" />
                              <MenuItem onClick={() => setShowCloneModal(true)}>
                                Clone into new beer
                              </MenuItem>
                              <MenuItem onClick={window.print}>Print</MenuItem>
                            </DropdownButton>
                          </div>
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                          <Divider />
                        </div>
                        {!currentRecipe.brewable && (
                          <div>
                            {!alreadyBrewed() ? (
                              <Alert bsStyle="danger" className="text-red">
                                Your recipe cannot be brewed because it does not
                                meet the requirements of the MiniBrew system.
                                You can see why by going to the Edit page for
                                this beer.
                              </Alert>
                            ) : (
                              <Alert bsStyle="danger" className="text-red">
                                Your recipe cannot be brewed because it no
                                longer meets the requirements of the MiniBrew
                                system. Create a new version to see what you
                                need to change.
                              </Alert>
                            )}
                          </div>
                        )}
                        <div className="recipe-detail__brew-water">
                          <div
                            className="minibrew-container"
                            style={{ marginBottom: "20px" }}
                          >
                            <div>
                              <h2
                                style={{
                                  fontWeight: "normal",
                                  fontFamily: "Gotham-Bold",
                                  margin: 0,
                                  display: "inline-block",
                                }}
                              >
                                Notes
                              </h2>
                              <i
                                style={{ float: "right", cursor: "pointer" }}
                                className="icon ion-edit"
                                onClick={() => setShowEditModal(true)}
                              />
                            </div>
                            <Tabs
                              id="notes-tabs"
                              activeKey={activeNoteKey}
                              onSelect={(key) => setActiveNoteKey(key)}
                            >
                              <Tab eventKey={1} title="PRIVATE">
                                <div style={{ whiteSpace: "pre-line" }}>
                                  <p
                                    style={{
                                      fontWeight: "normal",
                                      fontFamily: "Gotham-Book",
                                    }}
                                  >
                                    {currentRecipe.private_note}
                                  </p>
                                </div>
                              </Tab>
                              <Tab eventKey={2} title="PUBLIC">
                                <div style={{ whiteSpace: "pre-line" }}>
                                  <p
                                    style={{
                                      fontWeight: "normal",
                                      fontFamily: "Gotham-Book",
                                    }}
                                  >
                                    {currentRecipe.public_note}
                                  </p>
                                </div>
                              </Tab>
                            </Tabs>
                          </div>

                          <BrewWater
                            batchSize={6}
                            totalWater={currentRecipe.water_amount}
                            preMixWater={mashInWater}
                            kettleWater={
                              currentRecipe.water_amount - mashInWater
                            }
                          />

                          <div
                            className="minibrew-container"
                            style={{ marginBottom: "20px" }}
                          >
                            <h2
                              style={{
                                fontWeight: "normal",
                                fontFamily: "Gotham-Bold",
                                marginTop: "3px",
                              }}
                            >
                              Specifications
                            </h2>
                            <div className="row">
                              <div className="col-md-6 col-sm-12">
                                <p
                                  style={{
                                    fontWeight: "normal",
                                    fontFamily: "Gotham-Book",
                                  }}
                                >
                                  Target temperatures and carbonation level
                                  configured for this recipe.
                                </p>
                              </div>
                              <div className="col-md-6 col-sm-12">
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    marginTop: "4px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: "16px",
                                    }}
                                  >
                                    <p style={{ margin: 0 }}>
                                      Serving Temperature
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        flexShrink: 0,
                                        textAlign: "right",
                                        fontFamily: "Gotham-Medium",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {currentRecipe.serving_temperature
                                        ? `${currentRecipe.serving_temperature} °C`
                                        : "-"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: "16px",
                                    }}
                                  >
                                    <p style={{ margin: 0 }}>
                                      Chill / Pitch Temperature
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        flexShrink: 0,
                                        textAlign: "right",
                                        fontFamily: "Gotham-Medium",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {currentRecipe.chilling_temperature
                                        ? `${currentRecipe.chilling_temperature} °C`
                                        : "-"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: "16px",
                                    }}
                                  >
                                    <p style={{ margin: 0 }}>Carbonation</p>
                                    <p
                                      style={{
                                        margin: 0,
                                        flexShrink: 0,
                                        textAlign: "right",
                                        fontFamily: "Gotham-Medium",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {(currentRecipe.carbonation ||
                                        beer?.carbonation) &&
                                      parseFloat(
                                        currentRecipe.carbonation ||
                                          beer?.carbonation,
                                      ) > 0
                                        ? `${parseFloat(
                                            currentRecipe.carbonation ||
                                              beer?.carbonation,
                                          ).toFixed(2)} g/L CO2`
                                        : "-"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <Mashing
                          header={header(1, "Mash")}
                          currentRecipe={currentRecipe}
                        />
                        <Boil
                          header={header(2, "Boil")}
                          user={userInfo?.user}
                          currentRecipe={currentRecipe}
                        />
                        <Fermentation
                          header={header(3, "Fermentation")}
                          currentRecipe={currentRecipe}
                        />
                      </div>
                    </Tab>
                    <Tab eventKey={2} title="BREW HISTORY">
                      <div className="container-fluid">
                        <div className="row">
                          <div className="col-md-10 col-md-offset-1">
                            <BrewHistory sessions={sessions} />
                          </div>
                        </div>
                      </div>
                    </Tab>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
          <BrewModal
            show={showBrewModal}
            handleClose={() => setShowBrewModal(false)}
            selectedRecipe={currentRecipe}
          />
          <StartFermentationModal
            show={showFermentationModal}
            handleClose={() => setShowFermentationModal(false)}
            recipe={currentRecipe}
          />
          <EditModal
            show={showEditModal}
            recipe={currentRecipe}
            beer={beer}
            type={noteType}
            closeEditModal={() => setShowEditModal(false)}
            refetchRecipes={init}
          />
          <CloneModal
            show={showCloneModal}
            recipe={currentRecipe}
            closeCloneModal={() => setShowCloneModal(false)}
            beerstyleID={beer?.style?.id}
            reload={init}
          />
        </div>
      )}
      {!recipeLoaded && <RecipeDetailLoading />}
      {!loadingBar?.default && !isPageReady && recipeLoaded && (
        <NoRecipesFound url={window.location.pathname} />
      )}
    </div>
  );
}

RecipeDetail.propTypes = {
  match: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object,
};

export default RecipeDetail;
